<template>
  <div class="mt-6">
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-3">
      {{ $t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.TITLE', { name: queueName }) }}
    </h2>

    <woot-tabs :index="activeTab" @change="activeTab = $event">
      <woot-tabs-item
        v-for="tab in tabs"
        :key="tab.key"
        :name="tab.name"
        :count="tab.count"
      />
    </woot-tabs>

    <!-- ── QM §5.5.2 Unassigned ───────────────────────────────────────────── -->
    <div v-if="activeTab === 0" class="mt-3">
      <!-- QM §5.5.2 — supervisor actions ride on `Queues Management` update. -->
      <div
        v-if="selected.length && canActOnQueueSupervision"
        class="flex items-center gap-2 mb-2"
      >
        <span class="text-sm text-slate-600 dark:text-slate-400">
          {{
            $t('QUEUES_MGMT.SUPERVISOR.BULK.SELECTED', {
              count: selected.length,
            })
          }}
        </span>
        <woot-button
          size="tiny"
          icon="person-add"
          @click="openAssign(selected)"
        >
          {{ $t('QUEUES_MGMT.SUPERVISOR.BULK.ASSIGN') }}
        </woot-button>
        <woot-button
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="arrow-swap"
          @click="openChangeQueue(selected)"
        >
          {{ $t('QUEUES_MGMT.SUPERVISOR.BULK.CHANGE_QUEUE') }}
        </woot-button>
      </div>

      <woot-loading-state
        v-if="isLoadingUnassigned"
        :message="$t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.LOADING')"
      />
      <p v-else-if="!unassigned.length" class="text-slate-500">
        {{ $t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.EMPTY') }}
      </p>
      <table v-else class="woot-table">
        <thead>
          <th>
            <!-- QM §5.10 — bare checkboxes have no accessible name; screen readers would
                 announce them as unlabelled. -->
            <input
              type="checkbox"
              :aria-label="$t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.SELECT_ALL')"
              :checked="allSelected"
              @change="toggleAll($event.target.checked)"
            />
          </th>
          <th
            v-for="header in $t(
              'QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.TABLE_HEADER'
            )"
            :key="header"
          >
            {{ header }}
          </th>
        </thead>
        <tbody>
          <tr v-for="row in unassigned" :key="row.conversation_id">
            <td>
              <input
                type="checkbox"
                :aria-label="
                  $t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.SELECT_ONE', {
                    id: row.conversation_id,
                  })
                "
                :checked="selected.includes(row.conversation_id)"
                @change="toggleOne(row.conversation_id)"
              />
            </td>
            <td>
              <router-link :to="conversationUrl(row.conversation_id)">
                #{{ row.conversation_id }}
              </router-link>
              <p class="text-xs text-slate-500 mb-0">
                {{ row.subject || row.contact_name || '—' }}
              </p>
            </td>
            <td class="text-xs">{{ slaLabel(row) }}</td>
            <td class="text-xs">{{ relativeTime(row.created_at) }}</td>
            <td class="text-xs">{{ relativeTime(row.enqueued_at) }}</td>
            <td class="text-xs">{{ expansionLabel(row.expansion_trigger) }}</td>
            <td class="text-xs">{{ row.excluded_agent_name || '—' }}</td>
            <td class="button-wrapper">
              <template v-if="canActOnQueueSupervision">
                <woot-button
                  v-tooltip.top="$t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.ASSIGN')"
                  variant="smooth"
                  size="tiny"
                  icon="person-add"
                  @click="openAssign([row.conversation_id])"
                />
                <woot-button
                  v-tooltip.top="
                    $t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.CHANGE_QUEUE')
                  "
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="arrow-swap"
                  @click="openChangeQueue([row.conversation_id])"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <queue-pager :meta="unassignedMeta" @page="loadUnassigned" />
    </div>

    <!-- ── QM §5.5.2 Assigned ─────────────────────────────────────────────── -->
    <div v-else-if="activeTab === 1" class="mt-3">
      <woot-loading-state
        v-if="isLoadingAssigned"
        :message="$t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.LOADING')"
      />
      <p v-else-if="!assigned.length" class="text-slate-500">
        {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGNED_TABLE.EMPTY') }}
      </p>
      <table v-else class="woot-table">
        <thead>
          <th
            v-for="header in $t(
              'QUEUES_MGMT.SUPERVISOR.ASSIGNED_TABLE.TABLE_HEADER'
            )"
            :key="header"
          >
            {{ header }}
          </th>
        </thead>
        <tbody>
          <tr v-for="row in assigned" :key="row.conversation_id">
            <td>
              <router-link :to="conversationUrl(row.conversation_id)">
                #{{ row.conversation_id }}
              </router-link>
              <p class="text-xs text-slate-500 mb-0">
                {{ row.subject || row.contact_name || '—' }}
              </p>
            </td>
            <td class="text-xs">{{ row.owner_name || '—' }}</td>
            <td class="text-xs">{{ row.status }}</td>
            <td class="text-xs">
              {{ relativeTime(row.last_agent_activity_at) }}
            </td>
            <td>
              <span
                v-if="row.locked"
                class="inline-flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200"
              >
                <fluent-icon icon="lock-closed" size="12" />
                {{ $t('QUEUES_MGMT.MY_TICKETS.LOCKED') }}
              </span>
              <span v-else class="text-xs text-slate-400">—</span>
            </td>
            <td class="text-xs">{{ slaLabel(row) }}</td>
            <td class="button-wrapper">
              <template v-if="canActOnQueueSupervision">
                <woot-button
                  v-tooltip.top="$t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.ASSIGN')"
                  variant="smooth"
                  size="tiny"
                  icon="person-add"
                  @click="openAssign([row.conversation_id], row.locked)"
                />
                <!-- QM §5.5.2, §4.9 — queue change is a per-ticket supervisor action on
                   assigned tickets too -->
                <woot-button
                  v-tooltip.top="
                    $t('QUEUES_MGMT.SUPERVISOR.DRILL_DOWN.CHANGE_QUEUE')
                  "
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="arrow-swap"
                  @click="openChangeQueue([row.conversation_id])"
                />
                <woot-button
                  v-if="row.locked"
                  v-tooltip.top="$t('QUEUES_MGMT.SUPERVISOR.FORCE_UNLOCK')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="lock-closed"
                  :is-loading="busyId === row.conversation_id"
                  @click="forceUnlock(row)"
                />
                <woot-button
                  v-tooltip.top="$t('QUEUES_MGMT.CONVERSATION.RETURN')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="warning"
                  icon="arrow-reply"
                  :is-loading="busyId === row.conversation_id"
                  @click="returnOnBehalf(row)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <queue-pager :meta="assignedMeta" @page="loadAssigned" />
    </div>

    <!-- ── QM §5.5.2 Agents in queue ──────────────────────────────────────── -->
    <div v-else class="mt-3">
      <woot-loading-state
        v-if="isLoadingAgents"
        :message="$t('QUEUES_MGMT.SUPERVISOR.AGENTS_TABLE.LOADING')"
      />
      <p v-else-if="!agents.length" class="text-slate-500">
        {{ $t('QUEUES_MGMT.SUPERVISOR.AGENTS_TABLE.EMPTY') }}
      </p>
      <table v-else class="woot-table">
        <thead>
          <th
            v-for="header in $t(
              'QUEUES_MGMT.SUPERVISOR.AGENTS_TABLE.TABLE_HEADER'
            )"
            :key="header"
          >
            {{ header }}
          </th>
        </thead>
        <tbody>
          <tr v-for="agent in agents" :key="agent.id">
            <td>{{ agent.name }}</td>
            <td class="text-xs">{{ agent.availability_status || '—' }}</td>
            <td class="text-xs">
              {{ agent.open_count }} / {{ agent.capacity_cap || '∞' }}
            </td>
            <td class="text-xs">
              {{
                $t(
                  `QUEUES_MGMT.SUPERVISOR.AGENTS_TABLE.GROUP.${agent.member_of.toUpperCase()}`
                )
              }}
            </td>
            <td class="text-xs">
              {{ relativeTime(agent.last_auto_assigned_at) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <assign-agent-modal
      v-if="assignIds.length && queue"
      :show="!!assignIds.length"
      :conversation-ids="assignIds"
      :queue="queue"
      :is-locked="assignLocked"
      @assigned="onHandled"
      @close="assignIds = []"
    />

    <change-queue-modal
      v-if="changeQueueIds.length"
      :show="!!changeQueueIds.length"
      :conversation-ids="changeQueueIds"
      :current-queue-id="queueId"
      @changed="onHandled"
      @close="changeQueueIds = []"
    />

    <woot-confirm-modal
      ref="returnConfirm"
      :title="$t('QUEUES_MGMT.SUPERVISOR.RETURN_ON_BEHALF.TITLE')"
      :description="$t('QUEUES_MGMT.SUPERVISOR.RETURN_ON_BEHALF.DESC')"
      :confirm-label="$t('QUEUES_MGMT.SUPERVISOR.RETURN_ON_BEHALF.YES')"
      :cancel-label="$t('QUEUES_MGMT.SUPERVISOR.RETURN_ON_BEHALF.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  formatDistanceToNowStrict,
  parseISO,
  differenceInSeconds,
} from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import QueueActionsAPI from 'dashboard/api/queueActions';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
// QM §5.5.2 — supplies `canActOnQueueSupervision`, which gates every action in the tables below.
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import AssignAgentModal from './AssignAgentModal.vue';
import ChangeQueueModal from './ChangeQueueModal.vue';
import QueuePager from './QueuePager.vue';

const SLA_WARNING_PERCENT = 80;
const EMPTY_META = { current_page: 1, total_pages: 0, total_count: 0 };
// QM §5.7 — a sweep or bulk move can push several of these in a burst
const RELOAD_DEBOUNCE_MS = 500;

export default {
  components: { AssignAgentModal, ChangeQueueModal, QueuePager },
  mixins: [alertMixin, queueSupervisionMixin],
  props: {
    queueId: {
      type: Number,
      required: true,
    },
    queueName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeTab: 0,
      unassigned: [],
      assigned: [],
      agents: [],
      selected: [],
      assignIds: [],
      // QM §5.5.3 — whether the ticket(s) currently opened in AssignAgentModal are locked
      assignLocked: false,
      changeQueueIds: [],
      busyId: null,
      isLoadingUnassigned: false,
      isLoadingAssigned: false,
      isLoadingAgents: false,
      unassignedMeta: EMPTY_META,
      assignedMeta: EMPTY_META,
      reloadTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      getQueue: 'queues/getQueue',
    }),
    queue() {
      const queue = this.getQueue(this.queueId);
      return queue.id ? queue : null;
    },
    tabs() {
      return [
        // QM §5.10 — badges count the whole queue, not just the page on screen.
        {
          key: 'unassigned',
          name: this.$t('QUEUES_MGMT.SUPERVISOR.TABS.UNASSIGNED'),
          count: this.unassignedMeta.total_count,
        },
        {
          key: 'assigned',
          name: this.$t('QUEUES_MGMT.SUPERVISOR.TABS.ASSIGNED'),
          count: this.assignedMeta.total_count,
        },
        {
          key: 'agents',
          name: this.$t('QUEUES_MGMT.SUPERVISOR.TABS.AGENTS'),
          count: this.agents.length,
        },
      ];
    },
    // QM §5.10 — `unassigned` only ever holds the current page, so select-all is
    // naturally page-scoped: it can never sweep in rows the supervisor cannot see.
    allSelected() {
      return (
        this.unassigned.length > 0 &&
        this.selected.length === this.unassigned.length
      );
    },
  },
  watch: {
    queueId: {
      immediate: true,
      handler() {
        this.selected = [];
        this.reload();
      },
    },
  },
  mounted() {
    // QM §5.2, §5.7 — supervisor view must reflect ticket state changes without
    // a manual reload (Unassigned/Assigned tabs, e.g. the Expansion column).
    window.bus.$on(BUS_EVENTS.CONVERSATION_UPDATED, this.onConversationUpdated);
  },
  beforeDestroy() {
    window.bus.$off(
      BUS_EVENTS.CONVERSATION_UPDATED,
      this.onConversationUpdated
    );
    clearTimeout(this.reloadTimer);
  },
  methods: {
    // QM §5.7 — reload when a ticket enters/changes in the queue being viewed, or
    // was already listed here (covers it leaving via queue change/assignment too).
    onConversationUpdated(data) {
      const matchesQueue = data?.routing_state?.queue_id === this.queueId;
      const isListed =
        this.unassigned.some(row => row.conversation_id === data?.id) ||
        this.assigned.some(row => row.conversation_id === data?.id);
      if (!matchesQueue && !isListed) return;

      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.reload(), RELOAD_DEBOUNCE_MS);
    },
    async reload() {
      await Promise.all([
        this.loadUnassigned(),
        this.loadAssigned(),
        this.loadAgents(),
      ]);
    },
    // QM §5.10 — paging clears the selection: the checked ids belong to the page we are leaving.
    async loadUnassigned(page = 1) {
      this.isLoadingUnassigned = true;
      this.selected = [];
      try {
        const { payload, meta } = await this.$store.dispatch(
          'queues/getConversations',
          { queueId: this.queueId, page }
        );
        this.unassigned = (payload || []).filter(Boolean);
        this.unassignedMeta = meta;
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ERROR'));
      } finally {
        this.isLoadingUnassigned = false;
      }
    },
    async loadAssigned(page = 1) {
      this.isLoadingAssigned = true;
      try {
        const { payload, meta } = await this.$store.dispatch(
          'queues/getAssignedConversations',
          { queueId: this.queueId, page }
        );
        this.assigned = (payload || []).filter(Boolean);
        this.assignedMeta = meta;
      } catch (error) {
        this.assigned = [];
        this.assignedMeta = EMPTY_META;
      } finally {
        this.isLoadingAssigned = false;
      }
    },
    async loadAgents() {
      this.isLoadingAgents = true;
      try {
        this.agents = await this.$store.dispatch(
          'queues/getQueueAgents',
          this.queueId
        );
      } catch (error) {
        this.agents = [];
      } finally {
        this.isLoadingAgents = false;
      }
    },
    conversationUrl(conversationId) {
      return frontendURL(
        `accounts/${this.currentAccountId}/conversations/${conversationId}`
      );
    },
    relativeTime(value) {
      if (!value) return '—';
      return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
    },
    expansionLabel(trigger) {
      const key = (trigger || 'none').toUpperCase();
      return this.$t(`QUEUES_MGMT.AGENT.BROWSE_MODAL.EXPANSION.${key}`);
    },
    // QM §5.10 Text, never colour alone.
    slaLabel(row) {
      if (row.sla_percent === null || row.sla_percent === undefined) {
        return this.$t('QUEUES_MGMT.MY_TICKETS.NO_SLA');
      }
      if (!row.sla_deadline) return `${row.sla_percent}%`;
      const deadline = parseISO(row.sla_deadline);
      if (differenceInSeconds(deadline, new Date()) <= 0) {
        return this.$t('QUEUES_MGMT.MY_TICKETS.SLA_BREACHED');
      }
      const left = this.$t('QUEUES_MGMT.MY_TICKETS.SLA_LEFT', {
        time: formatDistanceToNowStrict(deadline),
      });
      return row.sla_percent >= SLA_WARNING_PERCENT
        ? `${row.sla_percent}% · ${left}`
        : left;
    },
    toggleAll(checked) {
      this.selected = checked
        ? this.unassigned.map(row => row.conversation_id)
        : [];
    },
    toggleOne(conversationId) {
      this.selected = this.selected.includes(conversationId)
        ? this.selected.filter(id => id !== conversationId)
        : [...this.selected, conversationId];
    },
    // QM §5.5.3 — `locked` is only ever true for a single row from the Assigned tab;
    // the Unassigned tab (single or bulk) never carries a lock.
    openAssign(ids, locked = false) {
      this.assignIds = [...ids];
      this.assignLocked = locked;
    },
    openChangeQueue(ids) {
      this.changeQueueIds = [...ids];
    },
    onHandled() {
      this.selected = [];
      this.reload();
      this.$emit('changed');
    },
    // QM §5.5.3 — supervisor releases someone else's lock
    async forceUnlock(row) {
      this.busyId = row.conversation_id;
      try {
        await QueueActionsAPI.unlock(row.conversation_id);
        this.showAlert(this.$t('QUEUES_MGMT.CONVERSATION.TOAST.UNLOCKED'));
        await this.reload();
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ERROR')
        );
      } finally {
        this.busyId = null;
      }
    },
    // QM §5.5.4 — return on the owner's behalf; the backend excludes the owner, not the supervisor
    async returnOnBehalf(row) {
      const confirmed = await this.$refs.returnConfirm.showConfirmation();
      if (!confirmed) return;
      this.busyId = row.conversation_id;
      try {
        await QueueActionsAPI.returnToQueue(row.conversation_id);
        this.showAlert(this.$t('QUEUES_MGMT.CONVERSATION.TOAST.RETURNED'));
        await this.reload();
        this.$emit('changed');
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ERROR')
        );
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>
